import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiRefreshCw, FiX } from "react-icons/fi";
import { getApiUrl } from "../../../../config/api";
import {
  CycleCard,
  WeekCard,
} from "../../../../components/usageBilling/UsageSummary";
import UsageCurrencySwitch from "../../../../components/usageBilling/UsageCurrencySwitch";
import SpendBreakdown from "../../../../components/usageBilling/SpendBreakdown";
import WeeklyHistoryChart from "../../../../components/usageBilling/WeeklyHistoryChart";
import {
  fillWeeks,
  formatDay,
  formatUsdWithCode,
  resetDay,
  unpricedReasonLabel,
} from "../../../../components/usageBilling/usageFormat";

import LimitAmountField, { useLimitAmount } from "../../../../components/usageBilling/LimitAmountField";

const HISTORY_WEEKS = 8;

// One user's usage as they see it in "My AI usage", plus what only admins get:
// this week's unpriced calls and the user's limit overrides. Opening it brings
// the user's row up to the current week, as their next turn would.
//
// Rendered into document.body like MyUsageModal, and dark-mode aware like it,
// since it reuses the same cards.
export default function UserUsageModal({
  uid,
  groups,
  canEdit,
  currentUser,
  onClose,
  onSaved,
  language,
  formatMoney = formatUsdWithCode,
  currency = "USD",
  onCurrencyChange,
  rateState,
}) {
  const zh = language === "zh";
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/users/${encodeURIComponent(uid)}?weeks=${HISTORY_WEEKS}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setUsage(data);
    } catch (err) {
      console.error("Failed to load user usage:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, uid]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const group = usage ? groups.find((g) => g.group_id === usage.group_id) : null;
  const weeks = usage ? fillWeeks(usage, usage.history, HISTORY_WEEKS) : [];
  const neverUsed = usage && usage.turns === 0 && usage.history.length === 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-usage-title"
        className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2
              id="user-usage-title"
              className="text-lg font-bold text-gray-900 dark:text-white truncate"
            >
              {usage?.display_name || usage?.email || uid}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {usage?.display_name && usage?.email ? `${usage.email} · ` : ""}
              {usage &&
                (group?.name ||
                  usage.group_id ||
                  (zh ? "無群組" : "No group"))}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                loadUsage();
                rateState?.refresh();
              }}
              disabled={loading}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              title={zh ? "重新整理" : "Refresh"}
              aria-label={zh ? "重新整理" : "Refresh"}
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label={zh ? "關閉" : "Close"}
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-4">
          {rateState && onCurrencyChange && (
            <UsageCurrencySwitch
              currency={currency}
              onChange={onCurrencyChange}
              rateState={rateState}
              language={language}
            />
          )}
          {!usage && loading && (
            <div className="flex flex-col items-center gap-3 py-16">
              <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {zh ? "正在載入用量..." : "Loading usage..."}
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              <span>
                {zh ? "無法載入用量：" : "Couldn't load usage: "}
                {error}
              </span>
              <button
                type="button"
                onClick={loadUsage}
                disabled={loading}
                className="shrink-0 font-medium underline disabled:opacity-50"
              >
                {zh ? "重試" : "Retry"}
              </button>
            </div>
          )}

          {usage && (
            <div
              className={`space-y-4 transition-opacity ${loading ? "opacity-60" : ""}`}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <WeekCard
                  week={usage}
                  enforced={usage.enforced}
                  formatMoney={formatMoney}
                  language={language}
                  forAdmin
                />
                <CycleCard usage={usage} language={language} forAdmin formatMoney={formatMoney} />
              </div>

              <LimitsSection
                uid={uid}
                usage={usage}
                group={group}
                formatMoney={formatMoney}
                currency={currency}
                rateState={rateState}
                canEdit={canEdit}
                currentUser={currentUser}
                onSaved={() => {
                  loadUsage();
                  onSaved();
                }}
                language={language}
              />

              {neverUsed ? (
                <p className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {zh
                    ? "這位使用者還沒有使用過 AI。"
                    : "This user hasn't used AI yet."}
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-5">
                  <div className="min-w-0 md:col-span-2">
                    <SpendBreakdown
                      breakdown={usage.breakdown_usd}
                      formatMoney={formatMoney}
                      title={zh ? "本週費用明細" : "Where it went this week"}
                      language={language}
                    />
                  </div>
                  <section className="min-w-0 md:col-span-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {zh ? "過去幾週" : "Past weeks"}
                    </h3>
                    {weeks.length > 1 ? (
                      <WeeklyHistoryChart weeks={weeks} language={language} formatMoney={formatMoney} />
                    ) : (
                      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {zh
                          ? `過去幾週的用量將於${formatDay(resetDay(usage.week_end), language)}起顯示在這裡。`
                          : `Past weeks appear here from ${formatDay(resetDay(usage.week_end), language)}.`}
                      </p>
                    )}
                  </section>
                </div>
              )}

              {usage.unpriced_this_week.length > 0 && (
                <section className="rounded-xl border border-amber-200 dark:border-amber-800 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {zh ? "本週未計價的呼叫" : "Unpriced calls this week"}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {zh
                      ? "系統無法計價的呼叫，以 $0 計入，所以實際花費會比上面高。"
                      : "Calls the system couldn't price. They count as $0, so the real cost is higher than shown above."}
                  </p>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
                    {usage.unpriced_this_week.map((row) => (
                      <li
                        key={`${row.source_service}:${row.operation}:${row.model}:${row.reason}`}
                        className="flex items-center justify-between gap-3 py-2 text-gray-700 dark:text-gray-200"
                      >
                        <span>
                          {unpricedReasonLabel(row.reason, language)}
                          <span className="block text-xs font-mono text-gray-500 dark:text-gray-400">
                            {[row.source_service, row.operation, row.model]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </span>
                        </span>
                        <span className="tabular-nums font-semibold">
                          {row.calls}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// The three places a user's limit can come from, which one is in effect, and
// (for admin / manage_all_groups) the override editor. Precedence: this
// cycle's override, then the permanent one, then the group's limit.
function LimitsSection({
  uid,
  usage,
  group,
  canEdit,
  currentUser,
  onSaved,
  language,
  formatMoney,
  currency,
  rateState,
}) {
  const zh = language === "zh";
  const draft = useLimitAmount({ initialCurrency: currency, rateState });
  const [thisCycleOnly, setThisCycleOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { permanent_usd: permanent, this_month_usd: thisCycle } =
    usage.limit_overrides;
  const cycleEnds = formatDay(usage.cycle_end, language);

  const save = async (limit, currentMonthOnly) => {
    setSaving(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/users/${encodeURIComponent(uid)}/limit`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            monthly_usage_limit_usd: limit,
            current_month_only: currentMonthOnly,
          }),
        },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      draft.setText("");
      setThisCycleOnly(false);
      onSaved();
    } catch (err) {
      console.error("Failed to save user usage limit:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      key: "user_month",
      label: zh
        ? `僅本週期（至${cycleEnds}）`
        : `This cycle only (until ${cycleEnds})`,
      value: thisCycle,
      removable: true,
      currentMonthOnly: true,
    },
    {
      key: "user",
      label: zh ? "永久個人額度" : "Permanent override",
      value: permanent,
      removable: true,
      currentMonthOnly: false,
    },
    {
      key: "group",
      label: group
        ? zh
          ? `群組額度（${group.name}）`
          : `Group limit (${group.name})`
        : zh
          ? "群組額度（無群組）"
          : "Group limit (no group)",
      value: group ? group.monthly_limit_per_user_usd : null,
      removable: false,
    },
  ];

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {zh ? "每月額度（每 28 天）" : "Monthly limit (per 28-day cycle)"}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {zh
            ? "依序採用第一個有設定的額度：僅本週期 → 永久個人額度 → 群組額度。變更立即生效。"
            : "The first one set applies: this cycle only, then permanent, then the group's. Changes apply immediately."}
        </p>
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
        {rows.map((row) => {
          const inEffect = usage.limit_source === row.key;
          return (
            <li
              key={row.key}
              className="flex items-center justify-between gap-3 py-2"
            >
              <span className="text-gray-700 dark:text-gray-200">
                {row.label}
              </span>
              <span className="flex items-center gap-2">
                {inEffect && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                    {zh ? "使用中" : "In effect"}
                  </span>
                )}
                <span
                  className={`tabular-nums ${
                    row.value === null
                      ? "text-gray-400"
                      : "font-semibold text-gray-900 dark:text-white"
                  }`}
                >
                  {row.value === null ? (zh ? "未設定" : "Not set") : formatMoney(row.value)}
                </span>
                {canEdit && row.removable && row.value !== null && (
                  <button
                    type="button"
                    onClick={() => save(null, row.currentMonthOnly)}
                    disabled={saving}
                    className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                  >
                    {zh ? "移除" : "Remove"}
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {usage.limit_source === null && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {zh
            ? "這位使用者沒有任何適用的額度，限制開啟後也不會被封鎖。"
            : "No limit applies to this user, so they won't be blocked even with enforcement on."}
        </p>
      )}

      {canEdit && (
        <form
          className="space-y-3 pt-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.canSave && !saving) save(draft.usdLimit, thisCycleOnly);
          }}
        >
          <LimitAmountField
            id="user-limit"
            label={zh ? "設定個人額度" : "Set an override"}
            draft={draft}
            rateState={rateState}
            saving={saving}
            language={language}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={thisCycleOnly}
                disabled={saving}
                onChange={(e) => setThisCycleOnly(e.target.checked)}
                className="w-4 h-4 accent-orange-500"
              />
              {zh ? `僅本週期（至${cycleEnds}）` : `This cycle only (until ${cycleEnds})`}
            </label>
            <button
              type="submit"
              disabled={!draft.canSave || saving}
              className="sm:ml-auto px-4 py-1.5 rounded-lg text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
            >
              {saving ? (zh ? "儲存中..." : "Saving...") : zh ? "儲存" : "Save"}
            </button>
          </div>
        </form>
      )}
      {canEdit && draft.isZero && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {zh
            ? "0 表示限制開啟後，這位使用者無法使用 AI。"
            : "0 blocks this user entirely once enforcement is on."}
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </section>
  );
}
